import { useQuery } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { CustomerReportApiAdapter } from '../adapters';
import { CustomerReportResponse, CustomerFilters, CustomerReportItem, PaginationMeta } from '../models';

interface UseCustomerReportDataProps {
  filters?: CustomerFilters;
  enabled?: boolean;
}

export const useCustomerReportData = ({ filters, enabled = true }: UseCustomerReportDataProps = {}) => {
  const adapter = new CustomerReportApiAdapter(createHttpClient(apiClientWH));

  // Build stable query key with sorted object properties to prevent unnecessary refetches
  const buildQueryKey = (filters?: CustomerFilters): [string, Record<string, any>] => {
    if (!filters) return ['customer-report', {}];
    
    // Only include non-default values and sort object keys for stability
    const keyObject: Record<string, any> = {};
    
    if (filters.page && filters.page !== 1) keyObject.page = filters.page;
    if (filters.per_page && filters.per_page !== 25) keyObject.per_page = filters.per_page;
    if (filters.sorting?.column !== 'id') keyObject.sort_column = filters.sorting?.column;
    if (filters.sorting?.direction !== 'asc') keyObject.sort_direction = filters.sorting?.direction;
    if (filters.orders_prev_month_min !== undefined) keyObject.orders_min = filters.orders_prev_month_min;
    if (filters.orders_prev_month_max !== undefined) keyObject.orders_max = filters.orders_prev_month_max;
    if (filters.sale_type) keyObject.sale_type = filters.sale_type;
    if (filters.country_id !== undefined) keyObject.country_id = filters.country_id;
    if (filters.customer_origin && filters.customer_origin.length > 0) {
      keyObject.customer_origin = [...filters.customer_origin].sort(); // Sorted array for stability
    }
    
    return ['customer-report', keyObject];
  };

  const queryKey = buildQueryKey(filters);

  const query = useQuery<CustomerReportResponse, Error>({
    queryKey,
    queryFn: () => adapter.getCustomerReport(filters),
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error?.message?.includes('401') || error?.message?.includes('403')) {
        return false;
      }
      return failureCount < 3;
    },
    placeholderData: (previousData) => previousData,
  });

  return {
    customerData: query.data,
    customers: query.data?.data || [] as CustomerReportItem[],
    paginationMeta: query.data?.meta as PaginationMeta | undefined,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error?.message || null,
    refetch: query.refetch,
  };
};